import { PlayerRef } from "@remotion/player";

import React, { MouseEventHandler, useCallback } from "react";
import { Playhead } from "../playhead";

import { scrollbarStyle, TIMELINE_HEIGHT, TRACK_HEIGHT } from "../../constants";
import { useTimelineContext } from "../../context/use-timeline-context";
import { useTimelineSize } from "../../context/use-timeline-size";
import { useTimelineSeek } from "../../hooks/use-timeline-seek";
//import { BackspaceToDelete } from "../keyboard-shortcuts/backspace-to-delete";
import { SpaceToPlayPause } from "../keyboard-shortcuts/space-to-play-pause";
import Ticks, { TICKS_HEIGHT, TicksBackground } from "../ticks";
import { TrackContent } from "../track";
import {
  FullTimelineContainer,
  TicksContainer,
  TimelineWrapper,
  TracksContainer,
  TrackWrapper,
} from "./styled";

const style: React.CSSProperties = {
  height: TIMELINE_HEIGHT + TICKS_HEIGHT,
};

const containerStyles: React.CSSProperties = {
  ...scrollbarStyle,
  height: TIMELINE_HEIGHT + TICKS_HEIGHT,
};

export const TimelineContainer = ({
  children,
  timelineContainerRef,
}: {
  children: React.ReactNode;
  timelineContainerRef: React.RefObject<HTMLDivElement>;
}) => {
  return (
    <TimelineWrapper ref={timelineContainerRef}>{children}</TimelineWrapper>
  );
};

const TimelineScrollableContainer = ({
  timelineScrollableContainerRef,
  children,
}: {
  timelineScrollableContainerRef: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}) => {
  const { timelineWidth } = useTimelineSize();

  return (
    <div
      ref={timelineScrollableContainerRef}
      style={{
        width: timelineWidth,
        height: TIMELINE_HEIGHT - TICKS_HEIGHT,
      }}
    >
      {children}
    </div>
  );
};

export const Timeline = ({
  playerRef,
}: {
  playerRef: React.RefObject<PlayerRef | null>;
}) => {
  const {
    timelineRef,
    timelineScrollableContainerRef,
    tracks,
    fps,
    durationInFrames: totalDurationInFrames,
  } = useTimelineContext();

  const visibleFrames = Math.max(
    4 * fps,
    Math.round(totalDurationInFrames * 1.25)
  );

  const { setSelectedItem } = useTimelineContext();

  const { startDragging, stopDragging } = useTimelineSeek({
    containerRef: timelineScrollableContainerRef,
    playerRef,
    totalDurationInFrames: visibleFrames,
  });

  const onPointerDownEmptySpace: MouseEventHandler = useCallback(
    (e) => {
      if (e.target === timelineScrollableContainerRef.current) {
        setSelectedItem(null);
        return;
      }
    },
    [setSelectedItem, timelineScrollableContainerRef]
  );

  // TODO: Add DropHandler wrapping TracksContainer with <DropHandler playerRef={playerRef}>
  // import from "../drop-handler"

  return (
    <>
      <FullTimelineContainer ref={timelineRef} style={style}>
        <TracksContainer
          id="tracksContainer"
          style={containerStyles}
          onPointerDown={onPointerDownEmptySpace}
        >
          <TicksBackground />
          <TrackWrapper
            style={{
              minHeight: TIMELINE_HEIGHT + TICKS_HEIGHT,
              height: tracks.length * TRACK_HEIGHT + TICKS_HEIGHT,
            }}
          >
            <TicksContainer
              style={{
                height: TICKS_HEIGHT,
              }}
              onPointerDown={startDragging}
              onPointerUp={stopDragging}
            >
              <Ticks
                timelineHeight={TIMELINE_HEIGHT}
                visibleFrames={visibleFrames}
              />
            </TicksContainer>
            <TimelineScrollableContainer
              timelineScrollableContainerRef={timelineScrollableContainerRef}
            >
              {tracks.map((track, i) => (
                <TrackContent
                  key={track.id}
                  track={track}
                  trackIndex={i}
                  visibleFrames={visibleFrames}
                  playerRef={playerRef}
                />
              ))}
            </TimelineScrollableContainer>
            <Playhead visibleFrames={visibleFrames} playerRef={playerRef} />
          </TrackWrapper>
        </TracksContainer>
      </FullTimelineContainer>
      <SpaceToPlayPause playerRef={playerRef} />
      {/*<BackspaceToDelete />*/}
    </>
  );
};
